"use client";
import { TypePackageDetailItemSkeleton } from "@contentful/types";
import { faArrowRight } from "@fortawesome/pro-light-svg-icons";
import { faCheckCircle as faCheckCircleSolid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { notEmpty } from "@yardzen/js-util";
import { Badge } from "@yardzen/ui/components/badge";
import { Button } from "@yardzen/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@yardzen/ui/components/dialog";
import { H2 } from "@yardzen/ui/components/h2";
import { Link } from "@yardzen/ui/components/link";
import { Small } from "@yardzen/ui/components/small";
import { Typography } from "@yardzen/ui/components/typography";
import { cn } from "@yardzen/ui/lib/utils";
import { Entry } from "contentful";
import Image from "next/image";
import { useBuilderPartnerCheckoutHref } from "./builderPartnerCheckoutHref";
import { PackagePreviewModal } from "./PackagePreviewModal";
import { hasPromoPrice, showsPromoTotal } from "./pricing";

export const PackageCard = ({
  package_card,
  keyVal,
  showImages,
  packageDetailPageVariant,
  packageId,
  cardFlagHidden,
  planComparisonModule,
  checkoutV2Enabled,
  showListItems,
}: {
  package_card: Entry<
    TypePackageDetailItemSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS"
  >;
  keyVal: string;
  showImages?: boolean;
  packageDetailPageVariant?: boolean;
  packageId?: string | null;
  cardFlagHidden?: boolean;
  planComparisonModule?: boolean;
  checkoutV2Enabled?: boolean;
  /**
   * Renders the entry's "What's included" listItems checklist. Opt-in per
   * placement (Contentful toggle on the section) — packageDetailItem entries
   * carry listItems for other surfaces, so defaulting on would leak the
   * checklist into every card placement (EN-5565).
   */
  showListItems?: boolean;
}) => {
  const {
    heading,
    previewImage,
    price,
    promoPrice,
    detailCardFlag,
    detailCardPill,
    bulletPoints,
    bulletPointHeading,
    listItems,
    detailCardSubheading,
    detailCardDisclaimer,
    cta: checkoutCTA,
    packagesPageCta,
  } = package_card?.fields || {};
  const withBuilderPartnerParams = useBuilderPartnerCheckoutHref();
  const showPromoPrice = hasPromoPrice(price, promoPrice);
  const isHighlighted = packageId === package_card?.fields.uniqueIdentifier;
  const checkoutCTAUrl = withBuilderPartnerParams(
    checkoutV2Enabled && checkoutCTA?.fields.challengerURL
      ? checkoutCTA?.fields.challengerURL
      : checkoutCTA?.fields.url,
  );

  return (
    <div
      key={keyVal}
      className={cn(
        "flex w-full flex-col rounded-b-xl bg-white shadow-md md:max-w-md",
        isHighlighted && "border-brand-primary-darker border-2",
        !detailCardFlag && !cardFlagHidden && "mt-0 md:mt-8",
        cardFlagHidden && "rounded-t-xl",
      )}
    >
      {detailCardFlag && !cardFlagHidden && (
        <div className="bg-scheme-flag-bg text-scheme-flag-text flex h-8 w-full items-center justify-center p-2">
          <Small>{detailCardFlag}</Small>
        </div>
      )}
      <div className="flex flex-col items-start justify-center gap-4 p-6">
        <div className="flex w-full flex-col items-center gap-4">
          <div className="flex w-full flex-row items-center justify-between gap-4">
            <div className="flex flex-row items-center gap-2">
              <H2 className="!pb-0">{heading}</H2>
              {detailCardPill && (
                <div className="flex-0">
                  <Badge variant="zest">{detailCardPill}</Badge>
                </div>
              )}
            </div>
            <div className="rounded-full border px-5 py-2.5">
              {showPromoPrice ? (
                <div className="flex flex-row items-center gap-1.5">
                  <Typography
                    as="span"
                    variant="body"
                    color="secondary"
                    className="line-through"
                  >
                    ${price}
                  </Typography>
                  {showsPromoTotal(promoPrice) && (
                    <Typography as="span" variant="body-lg" weight="semibold">
                      ${promoPrice}
                    </Typography>
                  )}
                </div>
              ) : (
                <Typography as="span" variant="body-lg" weight="semibold">
                  ${price}
                </Typography>
              )}
            </div>
          </div>
          <div className="w-full text-left text-xl">{detailCardSubheading}</div>
        </div>
        <div className="relative h-full w-full overflow-hidden rounded-lg sm:max-h-[300px]">
          {showImages && previewImage?.fields.image?.fields.file?.url && (
            <Image
              src={`https:${previewImage?.fields.image?.fields.file?.url}`}
              alt={previewImage?.fields.image?.fields.file?.fileName}
              width={435}
              height={565}
              style={{
                objectFit: "cover",
                width: "100%",
                height: "100%",
              }}
            />
          )}
        </div>
        <div className="flex w-full flex-col gap-2">
          {bulletPointHeading && (
            <span className="font-semibold">{bulletPointHeading}</span>
          )}

          <div>
            {bulletPoints?.fields.listItems?.map((item, index) => (
              <div
                key={item?.sys?.id || index}
                className="flex flex-row items-center gap-2 py-1"
              >
                <FontAwesomeIcon
                  icon={faCheckCircleSolid}
                  className="text-scheme-check h-4 w-4 shrink-0"
                />
                <div
                  className={cn(
                    item?.fields?.boldLineItem ? "font-semibold" : "",
                  )}
                >
                  {item?.fields?.heading}
                </div>
              </div>
            ))}
          </div>

          {showListItems && listItems && (
            <div className="flex flex-col gap-2">
              <span className="text-brand-primary-darker text-xs font-semibold uppercase tracking-wider">
                {listItems.fields.heading ?? "What's included"}
              </span>
              <div className="flex flex-col gap-1.5">
                {listItems.fields.listItems
                  ?.filter(notEmpty)
                  .map((item, index) => (
                    <div
                      key={item?.sys?.id || index}
                      className="flex items-center gap-2.5"
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircleSolid}
                        className="text-scheme-check h-4 w-4 shrink-0"
                      />
                      <span
                        className={cn(
                          "text-sm",
                          item?.fields?.boldLineItem
                            ? "font-semibold"
                            : "font-normal",
                        )}
                      >
                        {item?.fields?.heading}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-auto flex flex-col">
        {detailCardDisclaimer && (
          <div className="px-4 py-2">
            <Small>{detailCardDisclaimer}</Small>
          </div>
        )}
        <div
          className={`mt-auto flex flex-none flex-row items-center ${planComparisonModule ? "justify-start" : "justify-center"} gap-2 px-4 pb-4 pt-2`}
        >
          {packageDetailPageVariant ? (
            <Button className="w-full" asChild>
              <Link href={checkoutCTAUrl}>
                {checkoutCTA?.fields.buttonText}{" "}
                <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </Button>
          ) : (
            <>
              {!planComparisonModule ? (
                <>
                  {checkoutCTA && (
                    <Button className="w-full" asChild>
                      <Link href={checkoutCTAUrl}>
                        {checkoutCTA?.fields.buttonText}{" "}
                        <FontAwesomeIcon icon={faArrowRight} />
                      </Link>
                    </Button>
                  )}
                  {packagesPageCta &&
                    // this is where the button will pop up the dialog
                    (!packagesPageCta?.fields.optionalPackageDetails ? (
                      <Button variant="outline" className="w-full" asChild>
                        <Link
                          href={withBuilderPartnerParams(
                            `${packagesPageCta?.fields.url}`,
                          )}
                        >
                          {packagesPageCta?.fields.buttonText}{" "}
                          <FontAwesomeIcon icon={faArrowRight} />
                        </Link>
                      </Button>
                    ) : (
                      <Dialog>
                        <VisuallyHidden>
                          <DialogTitle>
                            {packagesPageCta?.fields.buttonText}
                          </DialogTitle>
                        </VisuallyHidden>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="w-full">
                            {packagesPageCta?.fields.buttonText}{" "}
                            <FontAwesomeIcon icon={faArrowRight} />
                          </Button>
                        </DialogTrigger>
                        <DialogContent
                          className="!h-auto !w-full !max-w-7xl sm:!max-h-[650px] sm:!w-auto sm:!min-w-[650px] md:!min-w-[850px]"
                          dialogCloseClassName="z-30"
                        >
                          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[500px] bg-gradient-to-b from-[#ECF4BF] to-transparent" />
                          <div className="relative z-20">
                            <PackagePreviewModal
                              packageInfo={
                                packagesPageCta?.fields.optionalPackageDetails
                              }
                              cta={checkoutCTA}
                              checkoutV2={checkoutV2Enabled}
                            />
                          </div>
                        </DialogContent>
                      </Dialog>
                    ))}
                </>
              ) : (
                <>
                  {packagesPageCta && (
                    <Button className="w-auto !px-3 md:!px-6" asChild>
                      <Link
                        href={withBuilderPartnerParams(
                          `${packagesPageCta?.fields.url}`,
                        )}
                        className="!whitespace-nowrap !text-base"
                      >
                        {packagesPageCta?.fields.buttonText}{" "}
                        <FontAwesomeIcon icon={faArrowRight} />
                      </Link>
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
